import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback } from "react";
import styled, { css } from "styled-components";
import { styledTheme } from "../../../theme/styled-theme";
import { Tooltip } from "../../floating";
import { LockedBadge } from "../../lockedBadge/LockedBadge";
import { controlFontThemes, defaultControlSize, InputErrorMessage, NakedInput, NewControlWrapper, } from "../../newCommon/ControlsCommon";
import { Input } from "./Input";
import { LabelWithTooltip } from "./LabelWithTooltip";
import { MaskedInput } from "./MaskedInput";
const _NewInput = forwardRef(({ className, tooltipContent, error, hideErrorMessage, rightElement, leftElement, rightLabel, leftLabel, size = defaultControlSize, locked, lockedTooltip, edited, onClickWrapper, ...props }, ref) => {
    const placeholder = props.placeholder
        ? props.required && !props.label
            ? `${props.placeholder} *`
            : props.placeholder
        : undefined;
    const LockedTooltip = useCallback(({ children }) => lockedTooltip ? (_jsx(Tooltip, { content: lockedTooltip, delay: 0, children: children })) : (_jsx(_Fragment, { children: children })), [lockedTooltip]);
    return (_jsxs(Root, { className: className, "$disabled": props.disabled, "$size": size, "$invalid": !!error, children: [typeof props.label === "string" ? (_jsx(LabelWithTooltip, { label: props.label, tooltipContent: tooltipContent, required: props.required })) : (props.label), _jsxs(InputWrapper, { "$disabled": props.disabled, "$size": size, "$invalid": !!error, "$edited": !error && edited, "$locked": locked, "data-disabled": props.disabled, onClick: onClickWrapper, children: [leftLabel && (_jsx(Label, { "$size": size, "data-align": "left", children: leftLabel })), leftElement && (_jsx(ExtraElement, { "$size": size, "$align": "left", "data-align": "left", children: leftElement })), props.maskType ? (_jsx(MaskedInput, { ...props, placeholder: placeholder })) : (_jsx(Input, { ...props, placeholder: placeholder, ref: ref, disabled: props.disabled || locked, isClickable: locked && !!onClickWrapper })), rightElement && (_jsx(ExtraElement, { "$size": size, "$align": "right", "data-align": "right", children: rightElement })), rightLabel && (_jsx(Label, { "$size": size, "data-align": "right", children: rightLabel })), locked && (_jsx(LockedContainer, { children: _jsx(LockedTooltip, { children: _jsx("div", { children: _jsx(LockedBadge, { type: "tertiary" }) }) }) }))] }), error && !hideErrorMessage && _jsx(InputErrorMessage, { children: error })] }));
});
const Root = styled.div `
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  ${Input} {
    &:focus {
      &::placeholder {
        opacity: 0;
      }
    }
  }
`;
const ExtraElement = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;

  ${(p) => p.$align === "left"
    ? css `
          padding-left: ${labelAndExtraElementPaddings[p.$size]};
        `
    : p.$align === "right"
        ? css `
            padding-right: ${labelAndExtraElementPaddings[p.$size]};
          `
        : ""};
`;
const labelAndExtraElementPaddings = {
    "50": "3px",
    "100": styledTheme.spacing.sp4,
    "200": styledTheme.spacing.sp4,
    "400": styledTheme.spacing.sp16,
};
const labelAndExtraElementMinWidth = {
    "50": "18px",
    "100": "24px",
    "200": "24px",
    "400": "24px",
};
const leftLabelMargin = {
    "50": "2px 0 2px 2px",
    "100": "3px 0 3px 3px",
    "200": "3px 0 3px 3px",
    "400": "4px 0 4px 4px",
};
const rightLabelMargin = {
    "50": "2px 2px 2px 0",
    "100": "3px 3px 3px 0",
    "200": "3px 3px 3px 0",
    "400": "4px 4px 4px 0",
};
const Label = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;

  ${(p) => controlFontThemes[p.$size || defaultControlSize]};
  color: ${(p) => p.theme.systemColors.grey800};

  border-radius: 4px;

  &[data-align="left"] {
    margin: ${({ $size }) => leftLabelMargin[$size]};
  }

  &[data-align="right"] {
    margin: ${({ $size }) => rightLabelMargin[$size]};
  }

  padding: ${(p) => "0 " + labelAndExtraElementPaddings[p.$size]};

  min-width: ${(p) => labelAndExtraElementMinWidth[p.$size]};

  background: ${(p) => p.theme.systemColors.grey100};
`;
const LockedContainer = styled.div `
  display: flex;
  align-items: center;
`;
const sizeThemes = {
    "50": css `
    ${NakedInput} {
      min-height: 22px;
      padding: 0 8px;
    }

    ${LockedContainer} {
      padding-right: ${(p) => p.theme.spacing.sp8};
    }
  `,
    "100": css `
    ${NakedInput} {
      min-height: 30px;
      padding: 0 8px;
    }

    ${LockedContainer} {
      padding-right: ${(p) => p.theme.spacing.sp8};
    }
  `,
    "200": css `
    ${NakedInput} {
      min-height: 38px;
      padding: 0 8px;
    }

    ${LockedContainer} {
      padding-right: ${(p) => p.theme.spacing.sp12};
    }
  `,
    "400": css `
    ${NakedInput} {
      min-height: 54px;
      padding: 0 16px;
    }

    ${LockedContainer} {
      padding-right: ${(p) => p.theme.spacing.sp12};
    }
  `,
};
const InputWrapper = styled(NewControlWrapper) `
  position: relative;
  flex: 1;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  overflow: hidden;

  ${(p) => sizeThemes[p.$size || defaultControlSize]}

  ${(p) => p.$disabled &&
    css `
      ${Label} {
        background-color: ${p.theme.systemColors.grey300};
      }
    `}

  ${(p) => p.$invalid &&
    css `
      ${Label} {
        background-color: ${p.theme.systemColors.red400};
        color: #fff;
      }
    `}

  ${(p) => p.$edited &&
    css `
      ${Label} {
        background-color: ${p.theme.systemColors.blue100};
      }
    `}
`;
export const NewInput = Object.assign(_NewInput, {
    InputWrapper,
    Input,
    Element: ExtraElement,
    Label,
    Root,
    LockedContainer,
});
